import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { MessageCircle, Send } from 'lucide-react';
import { supabase } from '../../services/supabaseClient';
import { serviceRoleClient } from '../../services/serviceRoleClient';
import { ENV } from '../../config/env';
import { WhatsAppAutomationService } from '../../services/whatsappAutomationService';

const db = () => serviceRoleClient || supabase;

const phoneVariants = (phone) => {
  const digits = String(phone || '').replace(/\D/g, '');
  const phone10 = digits.length >= 10 ? digits.slice(-10) : digits;
  return [...new Set([phone, phone10, phone10 ? `91${phone10}` : null, digits || null].filter(Boolean))];
};

const phone10Of = (phone) => {
  const digits = String(phone || '').replace(/\D/g, '');
  return digits.length >= 10 ? digits.slice(-10) : '';
};

const currentUser = () => {
  try {
    return JSON.parse(localStorage.getItem('roadserve_current_user') || '{}');
  } catch {
    return {};
  }
};

const staffUserId = () => {
  const user = currentUser();
  if (user?.id && String(user.id).length > 20) return user.id;
  return ENV.WHATSAPP_SYSTEM_USER_ID || null;
};

const partnerLabel = (m, userName) => {
  const shop = String(m?.shop_name || m?.mechanic_name || m?.name || '').trim();
  const person = String(userName || m?.mechanic_name || '').trim();
  if (shop && person && shop.toLowerCase() !== person.toLowerCase()) return `${shop} · ${person}`;
  return shop || person || 'Partner';
};

const fmtDate = (value) => {
  if (!value) return '';
  return new Date(value).toLocaleString('en-IN', {
    day: '2-digit',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const ChatBubble = ({ mine, name, text, at, image, tone }) => {
  const mineCls =
    tone === 'staff'
      ? 'bg-indigo-600 text-white'
      : 'bg-teal-600 text-white';
  const nameCls = mine
    ? tone === 'staff'
      ? 'text-indigo-100'
      : 'text-teal-100'
    : 'text-slate-400';
  return (
    <div className={`flex ${mine ? 'justify-end' : 'justify-start'}`}>
      <div
        className={`max-w-[90%] rounded-lg px-3 py-2 text-sm ${
          mine ? mineCls : 'bg-white text-slate-800 border border-slate-200'
        }`}
      >
        {name ? (
          <p className={`mb-0.5 text-[11px] font-semibold ${nameCls}`}>{name}</p>
        ) : null}
        {image ? (
          <a href={image} target="_blank" rel="noreferrer" className="mb-1 block">
            <img src={image} alt="" className="max-h-36 rounded-md object-cover" />
          </a>
        ) : null}
        {text ? <p className="whitespace-pre-wrap break-words">{text}</p> : null}
        <p className={`mt-1 text-[10px] ${mine ? (tone === 'staff' ? 'text-indigo-100' : 'text-teal-100') : 'text-slate-400'}`}>
          {fmtDate(at)}
        </p>
      </div>
    </div>
  );
};

const waText = (m) => {
  const raw = String(m.content || '').trim();
  if (raw && raw !== '{}' && !raw.startsWith('{')) return raw;
  if (m.template_name) return `[Template] ${m.template_name}`;
  if (m.message_type && m.message_type !== 'text') return `[${m.message_type}]`;
  return raw || '—';
};

const appBubbleName = (m, customerName, mechById, userById) => {
  if (m.sender_role === 'staff') {
    const staff = userById[String(m.sender_user_id || '')];
    return staff ? `RoadServe · ${staff}` : 'RoadServe';
  }
  if (m.sender_role === 'partner') {
    return partnerLabel(mechById[String(m.mechanic_id)], userById[String(m.sender_user_id || '')]);
  }
  return customerName || 'Customer';
};

const isSupportThreadMsg = (m) =>
  !m?.mechanic_id || String(m.sender_role || '') === 'staff';

export default function ComplaintJobChat({
  requestId,
  phone,
  customerName,
  assignedMechanicId: _assignedMechanicId,
  embedded = false,
  defaultTab = 'support',
}) {
  const [loading, setLoading] = useState(true);
  const [appMsgs, setAppMsgs] = useState([]);
  const [waMsgs, setWaMsgs] = useState([]);
  const [mechById, setMechById] = useState({});
  const [userById, setUserById] = useState({});
  const [tab, setTab] = useState(defaultTab === 'wa' || defaultTab === 'app' ? defaultTab : 'support');
  const [draft, setDraft] = useState('');
  const [sending, setSending] = useState(false);
  const [sendError, setSendError] = useState('');
  const [waAccountId, setWaAccountId] = useState('');
  const listRef = useRef(null);

  const load = useCallback(async () => {
    if (!requestId) {
      setLoading(false);
      return;
    }
    const client = db();
    const variants = phoneVariants(phone);
    const [appRes, waRes] = await Promise.all([
      client
        .from('rsa_job_messages')
        .select('id, sender_role, sender_user_id, message, message_type, media_url, created_at, mechanic_id')
        .eq('service_request_id', requestId)
        .order('created_at', { ascending: true })
        .limit(200),
      variants.length
        ? client
            .from('whatsapp_messages')
            .select('id, direction, content, message_type, media_url, template_name, created_at, source')
            .in('contact_number', variants)
            .order('created_at', { ascending: false })
            .limit(80)
            .then((r) => {
              if (r.error) {
                return client
                  .from('whatsapp_messages')
                  .select('id, direction, content, message_type, media_url, created_at, source')
                  .in('contact_number', variants)
                  .order('created_at', { ascending: false })
                  .limit(80);
              }
              return r;
            })
        : Promise.resolve({ data: [] }),
    ]);
    const msgs = Array.isArray(appRes.data) ? appRes.data : [];
    setAppMsgs(msgs);
    setWaMsgs(Array.isArray(waRes.data) ? [...waRes.data].reverse() : []);

    const mechIds = [...new Set(msgs.map((m) => m.mechanic_id).filter(Boolean))];
    const userIds = [
      ...new Set(
        msgs
          .filter((m) => m.sender_user_id && (m.sender_role === 'partner' || m.sender_role === 'staff'))
          .map((m) => m.sender_user_id),
      ),
    ];
    const names = {};
    const usersMap = {};
    const [{ data: mechs }, { data: users }] = await Promise.all([
      mechIds.length
        ? client.from('mechanics').select('id, shop_name, mechanic_name, number').in('id', mechIds)
        : Promise.resolve({ data: [] }),
      userIds.length
        ? client.from('users').select('id, name').in('id', userIds)
        : Promise.resolve({ data: [] }),
    ]);
    for (const mech of mechs || []) names[String(mech.id)] = mech;
    for (const u of users || []) usersMap[String(u.id)] = String(u.name || '').trim();
    setMechById(names);
    setUserById(usersMap);
    setLoading(false);
  }, [requestId, phone]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    load().finally(() => {
      if (!cancelled) setLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [load]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const rows = await WhatsAppAutomationService.listAccounts();
        if (cancelled) return;
        const active = (rows || []).filter((a) => a && a.is_active !== false);
        const def = active.find((a) => a.is_default) || active[0];
        setWaAccountId(def?.id || '');
      } catch {
        /* ignore */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!requestId) return undefined;
    const channel = supabase
      .channel(`complaint-job-chat:${requestId}`)
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'rsa_job_messages', filter: `service_request_id=eq.${requestId}` },
        () => {
          load();
        },
      )
      .subscribe();
    const poll = setInterval(load, 8000);
    return () => {
      clearInterval(poll);
      supabase.removeChannel(channel);
    };
  }, [requestId, load]);

  useEffect(() => {
    const el = listRef.current;
    if (!el) return;
    el.scrollTop = el.scrollHeight;
  }, [tab, appMsgs.length, waMsgs.length]);

  const chattingNames = useMemo(
    () => [
      ...new Set(
        appMsgs
          .filter((m) => m.sender_role === 'partner')
          .map((m) => partnerLabel(mechById[String(m.mechanic_id)], userById[String(m.sender_user_id || '')])),
      ),
    ],
    [appMsgs, mechById, userById],
  );

  const supportMsgs = useMemo(() => appMsgs.filter(isSupportThreadMsg), [appMsgs]);
  const partnerMsgs = useMemo(
    () => appMsgs.filter((m) => m.mechanic_id && String(m.sender_role || '') !== 'staff'),
    [appMsgs],
  );

  const sendApp = async (text) => {
    const userId = staffUserId();
    if (!userId) {
      throw new Error('Login dubara karke try karo.');
    }
    const client = db();
    const insert = await client.from('rsa_job_messages').insert({
      service_request_id: requestId,
      mechanic_id: null,
      sender_user_id: userId,
      sender_role: 'staff',
      message: text,
      message_type: 'text',
    }).select('id').maybeSingle();
    if (!insert.error) return;

    const rpc = await client.rpc('rsa_chat_staff_send_message', {
      p_request_id: requestId,
      p_staff_user_id: userId,
      p_message: text,
      p_message_type: 'text',
      p_media_url: null,
    });
    if (!rpc.error) {
      if (rpc.data?.success) return;
      throw new Error(rpc.data?.error || 'App chat send failed');
    }
    const msg = String(insert.error.message || rpc.error.message || '');
    if (msg.includes('sender_role') || msg.includes('staff')) {
      throw new Error('SQL run karo: supabase/rsa-staff-job-chat.sql');
    }
    throw new Error(insert.error.message || rpc.error.message || 'App chat send failed');
  };

  const sendWhatsApp = async (text) => {
    const p10 = phone10Of(phone);
    if (!p10) throw new Error('Customer phone missing');
    if (!waAccountId) throw new Error('WhatsApp account select nahi hua. Admin → WhatsApp me account on karo.');
    const userId = staffUserId();
    if (!userId) throw new Error('Login dubara karke try karo.');
    const res = await fetch(`${ENV.SUPABASE_URL}/functions/v1/whatsapp-send`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        apikey: ENV.SUPABASE_ANON_KEY,
        Authorization: `Bearer ${ENV.SUPABASE_ANON_KEY}`,
      },
      body: JSON.stringify({
        to: `91${p10}`,
        type: 'text',
        content: text,
        contact_name: customerName || null,
        user_id: userId,
        source: 'human',
        account_id: waAccountId,
        sender_mode: 'specific_account',
      }),
    });
    const json = await res.json().catch(() => ({}));
    if (!res.ok || json?.success === false) {
      throw new Error(json?.error || `WhatsApp send failed (${res.status})`);
    }
  };

  const onSend = async () => {
    const text = draft.trim();
    if (!text || sending || !requestId) return;
    setSending(true);
    setSendError('');
    try {
      if (tab === 'wa') {
        await sendWhatsApp(text);
      } else {
        await sendApp(text);
      }
      setDraft('');
      await load();
    } catch (e) {
      setSendError(e?.message || 'Send failed');
    } finally {
      setSending(false);
    }
  };

  const onKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      onSend();
    }
  };

  const listMax = embedded ? 'max-h-52' : 'max-h-80';

  return (
    <div className={embedded ? '' : 'bg-white rounded-xl shadow-sm border border-gray-200 p-6'}>
      {!embedded ? (
        <div className="flex items-center gap-2 mb-4">
          <MessageCircle size={20} className="text-teal-600" />
          <h2 className="text-xl font-bold text-gray-900">Customer chat</h2>
        </div>
      ) : null}

      <div className="flex flex-wrap items-center gap-2 mb-3">
        <button
          type="button"
          onClick={() => setTab('support')}
          className={`rounded-md px-2 py-0.5 text-[11px] font-bold ${
            tab === 'support' ? 'bg-indigo-600 text-white' : 'bg-indigo-100 text-indigo-800'
          }`}
        >
          RoadServe support {supportMsgs.length}
        </button>
        <button
          type="button"
          onClick={() => setTab('app')}
          className={`rounded-md px-2 py-0.5 text-[11px] font-bold ${
            tab === 'app' ? 'bg-sky-600 text-white' : 'bg-sky-100 text-sky-800'
          }`}
        >
          App {partnerMsgs.length}
        </button>
        <button
          type="button"
          onClick={() => setTab('wa')}
          className={`rounded-md px-2 py-0.5 text-[11px] font-bold ${
            tab === 'wa' ? 'bg-emerald-600 text-white' : 'bg-emerald-100 text-emerald-800'
          }`}
        >
          WhatsApp {waMsgs.length}
        </button>
        {chattingNames.length ? (
          <span className="text-sm text-slate-600">Partner: {chattingNames.join(', ')}</span>
        ) : null}
      </div>

      {loading ? (
        <p className="text-sm text-gray-500">Loading chats…</p>
      ) : tab === 'wa' ? (
        <>
          <p className="mb-2 text-xs font-bold uppercase tracking-wide text-emerald-700">WhatsApp</p>
          <div ref={listRef} className={`${listMax} space-y-2 overflow-y-auto rounded-lg bg-slate-50 p-2`}>
            {waMsgs.length === 0 ? (
              <p className="text-sm text-gray-500">Is number pe WhatsApp nahi mila. Neeche se naya message bhej sakte ho.</p>
            ) : (
              waMsgs.map((m) => (
                <ChatBubble
                  key={m.id}
                  mine={m.direction === 'outbound'}
                  name={m.direction === 'outbound' ? 'RoadServe' : customerName || 'Customer'}
                  text={waText(m)}
                  image={m.media_url}
                  at={m.created_at}
                />
              ))
            )}
          </div>
        </>
      ) : tab === 'app' ? (
        <>
          <p className="mb-2 text-xs font-bold uppercase tracking-wide text-sky-700">
            App chat (customer · partner)
          </p>
          <div ref={listRef} className={`${listMax} space-y-2 overflow-y-auto rounded-lg bg-slate-50 p-2`}>
            {partnerMsgs.length === 0 ? (
              <p className="text-sm text-gray-500">Is job pe partner app chat nahi hua.</p>
            ) : (
              partnerMsgs.map((m) => (
                <ChatBubble
                  key={m.id}
                  mine={m.sender_role === 'partner'}
                  tone="partner"
                  name={appBubbleName(m, customerName, mechById, userById)}
                  text={m.message}
                  image={m.media_url}
                  at={m.created_at}
                />
              ))
            )}
          </div>
        </>
      ) : (
        <>
          <p className="mb-2 text-xs font-bold uppercase tracking-wide text-indigo-700">
            RoadServe support (customer live job)
          </p>
          <div ref={listRef} className={`${listMax} space-y-2 overflow-y-auto rounded-lg bg-slate-50 p-2`}>
            {supportMsgs.length === 0 ? (
              <p className="text-sm text-gray-500">
                Abhi support pe koi message nahi. Neeche likh ke customer ko bhejo — complaint open ke baad bhi chalega.
              </p>
            ) : (
              supportMsgs.map((m) => (
                <ChatBubble
                  key={m.id}
                  mine={m.sender_role === 'staff'}
                  tone={m.sender_role === 'staff' ? 'staff' : 'partner'}
                  name={appBubbleName(m, customerName, mechById, userById)}
                  text={m.message}
                  image={m.media_url}
                  at={m.created_at}
                />
              ))
            )}
          </div>
        </>
      )}

      {tab === 'app' ? (
        <p className="mt-2 text-[11px] text-slate-500">
          Customer ko reply karna ho to <button type="button" className="font-semibold text-indigo-700 underline" onClick={() => setTab('support')}>RoadServe support</button> tab use karo.
        </p>
      ) : (
        <div className="mt-3 rounded-lg border border-slate-200 bg-white p-2">
          <textarea
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={onKeyDown}
            rows={2}
            placeholder={
              tab === 'wa'
                ? 'WhatsApp pe customer ko message…'
                : 'RoadServe support pe customer ko message…'
            }
            className="w-full resize-none border-0 bg-transparent text-sm text-slate-800 outline-none placeholder:text-slate-400"
          />
          <div className="mt-1 flex items-center justify-between gap-2">
            <p className="text-[11px] text-slate-400">
              {tab === 'wa'
                ? 'WhatsApp 24h window chahiye'
                : 'Complaint open ke baad bhi customer live job pe dikhega'}
            </p>
            <button
              type="button"
              onClick={onSend}
              disabled={sending || !draft.trim()}
              className="inline-flex items-center gap-1 rounded-md bg-indigo-600 px-3 py-1.5 text-xs font-semibold text-white disabled:opacity-50"
            >
              <Send size={14} />
              {sending ? 'Sending…' : 'Send'}
            </button>
          </div>
          {sendError ? <p className="mt-1 text-xs text-red-600">{sendError}</p> : null}
        </div>
      )}
    </div>
  );
}
